import { alpha, createTheme, lighten, darken, Theme } from '@mui/material/styles';

// ==================== 색상 정의 ====================
// Dark 테마 색상
const cobalt = '#2b4cc0';
const cobaltLight = '#5a7dff';
const cobaltDark = '#1c2d7a';
const cobaltSky = '#7d9fff';
const cobaltIce = '#b3c9ff';
const midnight = '#0f1a34';
const nebula = '#293b6d';
const frost = '#f5f7ff';

// Light 테마 색상
const lightCobalt = '#3d65f5';
const lightCobaltLight = '#6b8aff';
const lightCobaltDark = '#2b4cc0';
const lightSky = '#e8f0ff';
const lightPaper = '#ffffff';
const lightBg = '#ffffff'; // 완전 흰색 배경 (변경됨)
const textDark = '#0d1b2a';
const textGray = '#415a77';

// 공통 색상
const successMain = '#4cd6a8';
const successDeep = '#1b7b5d';
const warningMain = '#ffb365';
const warningDeep = '#c97a27';
const dangerMain = '#ff7b7b';
const dangerDeep = '#d24040';

// ==================== 테마 생성 함수 ====================
export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const isDark = mode === 'dark';
  
  // Glass shadow 함수
  const glassShadow = (depth: number) =>
    isDark
      ? `0 ${depth}px ${20 + depth * 2}px ${alpha('#0b1120', 0.32)}`
      : `0 ${depth}px ${20 + depth * 2}px ${alpha('#000000', 0.08)}`;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? cobalt : lightCobalt,
        light: isDark ? cobaltLight : lightCobaltLight,
        dark: isDark ? cobaltDark : lightCobaltDark,
        contrastText: isDark ? frost : '#ffffff',
      },
      secondary: {
        main: isDark ? cobaltSky : lightCobaltLight,
        light: isDark ? cobaltIce : lightSky,
        dark: isDark ? cobalt : lightCobaltDark,
        contrastText: isDark ? frost : textDark,
      },
      success: {
        main: successMain,
      },
      warning: {
        main: warningMain,
      },
      error: {
        main: dangerMain,
      },
      background: {
        default: isDark ? lighten(midnight, 0.05) : lightBg,
        paper: isDark ? alpha(nebula, 0.9) : lightPaper,
      },
      text: {
        primary: isDark ? frost : textDark,
        secondary: isDark ? 'rgba(245, 247, 255, 0.78)' : textGray,
        disabled: isDark ? 'rgba(245, 247, 255, 0.48)' : 'rgba(13, 27, 42, 0.38)',
      },
      divider: isDark ? 'rgba(245, 247, 255, 0.12)' : 'rgba(13, 27, 42, 0.12)',
    },
    shape: {
      borderRadius: 18,
    },
    typography: {
      fontFamily:
        '"Pretendard Variable", "Pretendard", "Apple SD Gothic Neo", "Noto Sans KR", "Segoe UI", sans-serif',
      h1: {
        fontWeight: 600,
        fontSize: 'clamp(2.6rem, 1.6rem + 2.6vw, 3.9rem)',
        letterSpacing: '-0.02em',
        lineHeight: 1.12,
      },
      h2: {
        fontWeight: 600,
        fontSize: 'clamp(2.1rem, 1.3rem + 2.1vw, 3.2rem)',
        letterSpacing: '-0.015em',
        lineHeight: 1.16,
      },
      h3: {
        fontWeight: 600,
        fontSize: 'clamp(1.8rem, 1.1rem + 1.6vw, 2.6rem)',
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
      },
      h4: {
        fontWeight: 600,
        fontSize: 'clamp(1.55rem, 1rem + 1.2vw, 2.1rem)',
        letterSpacing: '-0.01em',
      },
      h5: {
        fontWeight: 600,
        fontSize: 'clamp(1.4rem, 0.95rem + 1vw, 1.8rem)',
        letterSpacing: '-0.006em',
      },
      h6: {
        fontWeight: 600,
        fontSize: 'clamp(1.1rem, 0.85rem + 0.6vw, 1.35rem)',
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: 'clamp(1rem, 0.8rem + 0.35vw, 1.12rem)',
        letterSpacing: '0.015em',
      },
      subtitle2: {
        fontWeight: 500,
        fontSize: 'clamp(0.92rem, 0.75rem + 0.3vw, 1.02rem)',
        letterSpacing: '0.02em',
      },
      body1: {
        fontSize: 'clamp(0.98rem, 0.75rem + 0.3vw, 1.1rem)',
        lineHeight: 1.64,
      },
      body2: {
        fontSize: 'clamp(0.9rem, 0.7rem + 0.25vw, 1.02rem)',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.065em',
        textTransform: 'uppercase',
      },
      caption: {
        fontSize: '0.78rem',
        letterSpacing: '0.05em',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? lighten(midnight, 0.05) : lightBg,
            backgroundImage: isDark
              ? 'radial-gradient(circle at 14% 12%, rgba(90, 125, 255, 0.20), transparent 52%), radial-gradient(circle at 84% 8%, rgba(125, 159, 255, 0.22), transparent 45%), linear-gradient(160deg, #111f3f 0%, #0f1b38 40%, #1c2f5b 100%)'
              : 'none', // 그라데이션 제거 - 단색 배경
            color: isDark ? frost : textDark,
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? alpha(cobaltLight, 0.45) : alpha(lightCobalt, 0.4),
            borderRadius: 999,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: isDark ? alpha('#13203f', 0.7) : alpha('#e8f0ff', 0.5),
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: isDark
              ? `linear-gradient(120deg, ${alpha(cobalt, 0.92)} 0%, ${alpha(cobaltLight, 0.9)} 100%)`
              : `linear-gradient(120deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.98) 100%)`,
            backdropFilter: 'blur(16px)',
            borderBottom: isDark
              ? `1px solid ${alpha('#ffffff', 0.08)}`
              : `1px solid ${alpha('#000000', 0.08)}`,
            boxShadow: isDark
              ? `0 18px 28px ${alpha('#13203f', 0.45)}`
              : `0 4px 20px ${alpha('#000000', 0.08)}`,
            color: isDark ? frost : textDark,
            '& .MuiIconButton-root': {
              color: isDark ? frost : textDark,
            },
            '& .MuiTypography-root': {
              color: isDark ? frost : textDark,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingInline: 'clamp(16px, 2.4vw, 32px)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: isDark
              ? 'linear-gradient(160deg, rgba(35, 48, 92, 0.92), rgba(24, 36, 74, 0.92))'
              : 'linear-gradient(160deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 255, 0.92))',
            border: isDark
              ? `1px solid ${alpha('#ffffff', 0.07)}`
              : `1px solid ${alpha('#000000', 0.08)}`,
            boxShadow: isDark
              ? `${glassShadow(6)}, inset 0 0 0 1px ${alpha('#ffffff', 0.02)}`
              : `${glassShadow(6)}, inset 0 0 0 1px ${alpha('#000000', 0.02)}`,
            backdropFilter: 'blur(18px)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            position: 'relative',
            overflow: 'hidden',
            padding: 'clamp(18px, 1.8vw, 28px)',
            borderRadius: 22,
            backgroundImage: isDark
              ? 'linear-gradient(165deg, rgba(40, 60, 118, 0.92), rgba(24, 37, 74, 0.88))'
              : 'linear-gradient(165deg, rgba(255, 255, 255, 0.95), rgba(245, 248, 255, 0.9))',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-20% -30% auto auto',
              width: '60%',
              height: '60%',
              background: isDark
                ? `radial-gradient(circle at top right, ${alpha(cobaltLight, 0.5)}, transparent 60%)`
                : `radial-gradient(circle at top right, ${alpha(lightCobaltLight, 0.3)}, transparent 60%)`,
              pointerEvents: 'none',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 500,
            letterSpacing: '0.025em',
            backgroundImage: isDark
              ? `linear-gradient(135deg, ${alpha(cobaltLight, 0.22)}, ${alpha(cobalt, 0.42)})`
              : `linear-gradient(135deg, ${alpha(lightCobaltLight, 0.15)}, ${alpha(lightCobalt, 0.25)})`,
            border: isDark
              ? `1px solid ${alpha('#ffffff', 0.12)}`
              : `1px solid ${alpha('#000000', 0.12)}`,
            color: isDark ? frost : textDark,
            '&.MuiChip-colorSecondary': {
              backgroundImage: isDark
                ? `linear-gradient(135deg, ${alpha(cobaltSky, 0.3)}, ${alpha(cobaltIce, 0.55)})`
                : `linear-gradient(135deg, ${alpha(lightSky, 0.6)}, ${alpha(lightCobaltLight, 0.4)})`,
              color: isDark ? frost : textDark,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: 'clamp(18px, 2.4vw, 28px)',
            paddingBlock: '12px',
            letterSpacing: '0.09em',
            transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
            backgroundImage: isDark
              ? `linear-gradient(120deg, ${alpha(cobalt, 0.98)}, ${alpha(cobaltLight, 0.98)})`
              : `linear-gradient(120deg, ${alpha(lightCobalt, 0.95)}, ${alpha(lightCobaltLight, 0.92)})`,
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              transform: 'translateY(-2px)',
              filter: 'brightness(1.03)',
              boxShadow: 'none',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
          },
          containedSecondary: {
            backgroundImage: isDark
              ? `linear-gradient(120deg, ${alpha(cobaltSky, 0.9)}, ${alpha(cobaltIce, 0.92)})`
              : `linear-gradient(120deg, ${alpha(lightSky, 0.9)}, ${alpha(lightCobaltLight, 0.85)})`,
            color: isDark ? frost : textDark,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedSuccess: {
            backgroundImage: `linear-gradient(120deg, ${alpha(successMain, 0.94)}, ${alpha(successDeep, 0.94)})`,
            color: '#041a13',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              filter: 'brightness(1.04)',
            },
          },
          containedWarning: {
            backgroundImage: `linear-gradient(120deg, ${alpha(warningMain, 0.94)}, ${alpha(warningDeep, 0.94)})`,
            color: '#2b1404',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              filter: 'brightness(1.04)',
            },
          },
          containedError: {
            backgroundImage: `linear-gradient(120deg, ${alpha(dangerMain, 0.95)}, ${alpha(dangerDeep, 0.95)})`,
            color: frost,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              filter: 'brightness(1.04)',
            },
          },
          outlined: {
            borderWidth: 1.5,
            borderColor: isDark ? alpha(frost, 0.3) : alpha(lightCobalt, 0.4),
            background: 'transparent',
            color: isDark ? frost : lightCobalt,
            boxShadow: 'none',
            '&:hover': {
              borderColor: isDark ? alpha(frost, 0.5) : alpha(lightCobalt, 0.6),
              backgroundColor: isDark ? alpha(frost, 0.08) : alpha(lightCobalt, 0.08),
              boxShadow: 'none',
            },
          },
          outlinedSecondary: {
            borderWidth: 1.5,
            borderColor: isDark ? alpha(cobaltSky, 0.4) : alpha(lightCobalt, 0.5),
            background: 'transparent',
            color: isDark ? cobaltSky : lightCobalt,
            boxShadow: 'none',
            '&:hover': {
              borderColor: isDark ? alpha(cobaltSky, 0.6) : alpha(lightCobalt, 0.7),
              backgroundColor: isDark ? alpha(cobaltSky, 0.12) : alpha(lightCobalt, 0.1),
              boxShadow: 'none',
            },
          },
          text: {
            paddingInline: 12,
            color: isDark ? cobaltLight : lightCobalt,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: isDark ? alpha(frost, 0.08) : alpha('#ffffff', 0.8),
            border: isDark
              ? `1px solid ${alpha(frost, 0.08)}`
              : `1px solid ${alpha(lightCobalt, 0.15)}`,
            transition: 'border-color 160ms ease, box-shadow 160ms ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha(frost, 0.4) : alpha(lightCobalt, 0.4),
            },
            '&.Mui-focused': {
              backgroundColor: isDark ? alpha(frost, 0.12) : alpha('#ffffff', 0.95),
              boxShadow: isDark
                ? `0 0 0 3px ${alpha(cobaltLight, 0.2)}`
                : `0 0 0 3px ${alpha(lightCobalt, 0.15)}`,
              borderColor: isDark ? alpha(cobaltLight, 0.5) : alpha(lightCobalt, 0.5),
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha(frost, 0.18) : alpha(lightCobalt, 0.2),
            },
          },
          input: {
            color: isDark ? frost : textDark,
            padding: '14px 18px',
            '&::placeholder': {
              color: isDark ? alpha(frost, 0.6) : alpha(textGray, 0.6),
              opacity: 1,
            },
          },
          adornedEnd: {
            paddingRight: 16,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? alpha(frost, 0.72) : alpha(textGray, 0.7),
            '&.Mui-focused': {
              color: isDark ? frost : lightCobalt,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: isDark ? alpha(frost, 0.72) : alpha(textGray, 0.7),
            '&.Mui-focused': {
              color: isDark ? frost : lightCobalt,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: isDark
              ? 'linear-gradient(155deg, rgba(33, 47, 90, 0.95), rgba(24, 34, 66, 0.92))'
              : 'linear-gradient(155deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 255, 0.95))',
            border: isDark
              ? `1px solid ${alpha('#ffffff', 0.12)}`
              : `1px solid ${alpha('#000000', 0.08)}`,
            backdropFilter: 'blur(16px)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            marginInline: 4,
            color: isDark ? frost : textDark,
            '&:hover': {
              backgroundColor: isDark
                ? alpha(cobaltLight, 0.18)
                : alpha(lightCobalt, 0.12),
            },
            '&.Mui-selected': {
              backgroundColor: isDark
                ? alpha(cobaltLight, 0.24)
                : alpha(lightCobalt, 0.18),
              '&:hover': {
                backgroundColor: isDark
                  ? alpha(cobaltLight, 0.32)
                  : alpha(lightCobalt, 0.24),
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: 'background-color 160ms ease, transform 160ms ease',
            '&:hover': {
              backgroundColor: isDark
                ? alpha(cobaltLight, 0.18)
                : alpha(lightCobalt, 0.12),
              transform: 'translateX(4px)',
            },
            '&.Mui-selected': {
              backgroundImage: isDark
                ? `linear-gradient(120deg, ${alpha(cobalt, 0.42)}, ${alpha(cobaltLight, 0.52)})`
                : `linear-gradient(120deg, ${alpha(lightCobalt, 0.18)}, ${alpha(lightCobaltLight, 0.28)})`,
              boxShadow: isDark
                ? `inset 0 0 0 1px ${alpha(frost, 0.12)}`
                : `inset 0 0 0 1px ${alpha(lightCobalt, 0.15)}`,
              '&:hover': {
                backgroundImage: isDark
                  ? `linear-gradient(120deg, ${alpha(cobalt, 0.48)}, ${alpha(cobaltLight, 0.6)})`
                  : `linear-gradient(120deg, ${alpha(lightCobalt, 0.24)}, ${alpha(lightCobaltLight, 0.34)})`,
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? alpha(frost, 0.08) : alpha(textDark, 0.08),
          },
          head: {
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            color: isDark ? frost : textDark,
            backgroundColor: isDark
              ? alpha(cobaltLight, 0.16)
              : alpha(lightCobalt, 0.12),
          },
          body: {
            color: isDark ? alpha(frost, 0.88) : alpha(textDark, 0.9),
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 160ms ease',
            '&:nth-of-type(odd)': {
              backgroundColor: isDark ? alpha(frost, 0.02) : alpha(lightCobalt, 0.02),
            },
            '&:hover': {
              backgroundColor: isDark
                ? alpha(cobaltLight, 0.12)
                : alpha(lightCobalt, 0.08),
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? alpha(frost, 0.08) : alpha(textDark, 0.08),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 18,
            backgroundColor: isDark
              ? alpha(cobaltLight, 0.12)
              : alpha(lightCobalt, 0.08),
            border: isDark
              ? `1px solid ${alpha(cobaltLight, 0.25)}`
              : `1px solid ${alpha(lightCobalt, 0.2)}`,
            color: isDark ? frost : textDark,
          },
          icon: {
            color: isDark ? cobaltLight : lightCobalt,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 999,
            backgroundImage: isDark
              ? `linear-gradient(90deg, ${cobaltLight}, ${cobaltSky})`
              : `linear-gradient(90deg, ${lightCobalt}, ${lightCobaltLight})`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.025em',
            color: isDark ? alpha(frost, 0.7) : alpha(textGray, 0.7),
            '&.Mui-selected': {
              color: isDark ? frost : lightCobalt,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark
              ? alpha('#04060d', 0.92)
              : alpha('#0d1b2a', 0.9),
            color: isDark ? frost : '#ffffff',
            borderRadius: 12,
            border: isDark
              ? `1px solid ${alpha(frost, 0.1)}`
              : `1px solid ${alpha('#000000', 0.1)}`,
          },
        },
      },
    },
  });
};

// 기본 export (하위 호환성 유지)
export default createAppTheme('dark');
